import { Component, ViewChild } from '@angular/core';
import { NgFor, NgIf, NgStyle, NgSwitch, NgSwitchCase } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { ChatBot } from './components/chat-bot/chat-bot';

type ModalName = 'work' | 'about' | 'contact' | null;

interface ProjectPanel {
  kicker: string;
  title: string;
  summary: string;
  year: string;
  stack: string;
  stat: string;
  image: string;
  accent: string;
  opensChat?: boolean;
  externalUrl?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [NgFor, NgIf, NgStyle, NgSwitch, NgSwitchCase, RouterOutlet, ChatBot],
  template: `
    <router-outlet *ngIf="isAdminRoute"></router-outlet>

    <ng-container *ngIf="!isAdminRoute">
      <ng-container *ngIf="environment.production; else developmentSite">
        <main
          class="coming-shell"
          [ngStyle]="{
            '--pointer-x': pointerX + '%',
            '--pointer-y': pointerY + '%'
          }"
          (pointermove)="trackPointer($event)"
        >
          <header class="coming-topbar" aria-label="Production preview">
            <a class="brand" href="#" aria-label="Frantinco AI home">
              <span>Frantinco</span><strong>AI</strong>
            </a>
            <span>Production / frantinco-ai.com</span>
          </header>

          <section class="coming-hero" aria-label="Coming soon">
            <p class="coming-kicker">Frantinco AI is preparing something new</p>
            <h1>Coming Soon</h1>
            <p class="coming-copy">
              Website utama sedang kami siapkan. Untuk sementara, halaman ini jadi ruang tunggu resmi sebelum Frantinco AI hadir dengan visualizer, chatbot, katalog produk, dan fitur digital lainnya.
            </p>

            <div class="coming-status" aria-label="Launch progress">
              <span>Development aktif</span>
              <span>UI sedang dirapikan</span>
              <span>Production preview</span>
            </div>

            <button type="button" class="coming-project-button" (click)="openAbsensi()">
              Buka Absensi
            </button>
          </section>

          <button type="button" class="game-float" (click)="openTypingGame()">
            Main Typing
          </button>

          <section class="game-backdrop" *ngIf="gameOpen" (click)="closeTypingGame()" aria-modal="true" role="dialog">
            <div class="game-panel" (click)="$event.stopPropagation()">
              <button type="button" class="modal-close" (click)="closeTypingGame()" aria-label="Close typing game">Close</button>

              <p class="modal-kicker">Mini Game</p>
              <h2>Tes Kecepatan Mengetik</h2>
              <p class="game-instruction">
                Ketik kalimat Bahasa Indonesia di bawah ini secepat dan seakurat mungkin.
              </p>

              <div class="typing-target">
                {{ currentTypingPhrase }}
              </div>

              <textarea
                class="typing-input"
                [value]="typingInput"
                (input)="onTypingInput($any($event.target).value)"
                placeholder="Mulai ketik di sini..."
                aria-label="Typing game input"
              ></textarea>

              <div class="game-stats">
                <article>
                  <span>{{ typingWpm }}</span>
                  <p>WPM</p>
                </article>
                <article>
                  <span>{{ typingAccuracy }}%</span>
                  <p>Akurasi</p>
                </article>
                <article>
                  <span>{{ typingElapsed }}s</span>
                  <p>Waktu</p>
                </article>
              </div>

              <div class="game-actions">
                <p *ngIf="typingComplete">Mantap bro, kalimat selesai diketik.</p>
                <button type="button" (click)="nextTypingPhrase()">Kalimat Baru</button>
                <button type="button" (click)="restartTypingGame()">Ulangi</button>
              </div>
            </div>
          </section>
        </main>
      </ng-container>

      <ng-template #developmentSite>
        <main class="site-shell">
      <header class="topbar" aria-label="Primary">
        <a class="brand" href="#" aria-label="Frantinco AI home">
          <span>Frantinco</span><strong>AI</strong>
        </a>

        <nav class="nav-links" aria-label="Portfolio navigation">
          <button type="button" (click)="openModal('work')">Work</button>
          <button type="button" (click)="openModal('about')">About</button>
          <button type="button" (click)="openModal('contact')">Contact</button>
        </nav>
      </header>

      <section class="panel-stage" aria-label="Featured work">
        <article
          class="project-panel"
          *ngFor="let project of projects; let index = index"
          [ngStyle]="{
            '--accent': project.accent,
            '--panel-image': 'url(' + project.image + ')'
          }"
        >
          <div class="panel-index">0{{ index + 1 }}</div>
          <h2>{{ project.kicker }}</h2>
          <div class="panel-copy">
            <p class="eyebrow">{{ project.year }} / {{ project.stack }}</p>
            <h3>{{ project.title }}</h3>
            <p>{{ project.summary }}</p>
            <div class="panel-meta">
              <span>{{ project.stat }}</span>
              <button type="button" (click)="viewProject(project)">View Project</button>
            </div>
          </div>
        </article>
      </section>

      <footer class="footer-strip">
        <span>
          Selected builds, AI workflows, interfaces, and launch systems.
          <strong *ngIf="!environment.production">Dev Mode</strong>
        </span>
        <span>Jakarta / Remote</span>
      </footer>

      <section class="modal-backdrop" *ngIf="activeModal" (click)="closeModal()" aria-modal="true" role="dialog">
        <div class="modal-panel" (click)="$event.stopPropagation()">
          <button type="button" class="modal-close" (click)="closeModal()" aria-label="Close dialog">Close</button>

          <ng-container [ngSwitch]="activeModal">
            <div *ngSwitchCase="'work'">
              <p class="modal-kicker">Work Archive</p>
              <h2>Project list siap lo ganti sesuai kerjaan asli.</h2>
              <div class="archive-grid">
                <article *ngFor="let project of projects">
                  <span>{{ project.year }}</span>
                  <h3>{{ project.title }}</h3>
                  <p>{{ project.stack }}</p>
                </article>
              </div>
            </div>

            <div *ngSwitchCase="'about'">
              <p class="modal-kicker">About</p>
              <h2>Frantinco AI is a sharp digital workspace for experiments, automation, and production-ready builds.</h2>
              <p class="modal-body">
                Use this section to explain who you are, what kind of work you take,
                your process, and the outcomes clients can expect.
              </p>
            </div>

            <div *ngSwitchCase="'contact'">
              <p class="modal-kicker">Contact</p>
              <h2>Start a build, audit an idea, or bring a messy workflow into shape.</h2>
              <form class="contact-form">
                <label>
                  Name
                  <input type="text" placeholder="Your name">
                </label>
                <label>
                  Email
                  <input type="email" placeholder="you@email.com">
                </label>
                <label>
                  Project
                  <textarea placeholder="Tell me what you want to build"></textarea>
                </label>
                <button type="button">Send Message</button>
              </form>
            </div>
          </ng-container>
        </div>
      </section>
        </main>
      </ng-template>
    </ng-container>

    <app-chat-bot *ngIf="!isAdminRoute" [showLauncher]="false"></app-chat-bot>
  `
})
export class AppComponent {
  @ViewChild(ChatBot) private chatBot?: ChatBot;

  environment = environment;
  activeModal: ModalName = null;
  isAdminRoute = false;
  gameOpen = false;
  pointerX = 50;
  pointerY = 50;
  typingInput = '';
  typingStartedAt: number | null = null;
  typingElapsed = 0;
  typingWpm = 0;
  typingAccuracy = 100;
  typingComplete = false;
  typingPhraseIndex = 0;

  typingPhrases = [
    'Frantinco AI membantu pekerjaan digital menjadi lebih cepat dan rapi.',
    'Setiap ide besar dimulai dari langkah kecil yang dikerjakan hari ini.',
    'Teknologi yang baik harus mudah dipakai oleh tim dan pelanggan.',
    'Website ini sedang disiapkan untuk pengalaman yang lebih matang.'
  ];

  projects: ProjectPanel[] = [
    {
      kicker: 'Visualizer',
      title: 'Frantinco AI Visualizer',
      summary: 'Platform visual interaktif untuk menampilkan ide, alur kerja, dan data operasional Frantinco agar lebih mudah dipahami oleh tim maupun klien.',
      year: '2026',
      stack: 'AI / Visual Tools',
      stat: 'Interactive insight preview',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=80',
      accent: '#c8ff32',
      externalUrl: 'https://dev.visualizer.frantinco-ai.com/'
    },
    {
      kicker: 'Chatbot',
      title: 'Chatbot Frantinco',
      summary: 'Asisten percakapan untuk membantu kebutuhan teknis, menjawab pertanyaan produk, dan mengarahkan calon pelanggan agar lebih mudah terhubung dengan tim sales.',
      year: '2026',
      stack: 'AI / Customer Support',
      stat: 'Teknis dan sales assistant',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1200&q=80',
      accent: '#ff6b4a',
      opensChat: true,
      externalUrl: 'https://dev.chatbot.frantinco-ai.com/'
    },
    {
      kicker: 'Absensi',
      title: 'Absen Frantinco',
      summary: 'Sistem absensi digital untuk mencatat kehadiran, memantau aktivitas, dan membantu rekap data karyawan dengan proses yang lebih cepat dan tertata.',
      year: '2025',
      stack: 'HR / Operations',
      stat: 'Digital attendance record',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=80',
      accent: '#5eead4',
      externalUrl: environment.absensiUrl
    },
    {
      kicker: 'E-katalog',
      title: 'Produk Frantinco',
      summary: 'Katalog produk digital yang menyajikan informasi produk Frantinco secara rapi, mudah dicari, dan siap dibagikan kepada pelanggan atau partner.',
      year: '2025',
      stack: 'Product / Catalog',
      stat: 'Produk lebih mudah diakses',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
      accent: '#f7c948'
    },
    {
      kicker: 'Coming Soon',
      title: 'Fitur yang akan hadir',
      summary: 'Ruang pengembangan untuk fitur baru Frantinco, mulai dari otomasi internal, integrasi data, sampai tools berbasis AI yang akan terus ditambahkan.',
      year: '2024',
      stack: 'Roadmap / Innovation',
      stat: 'Next feature pipeline',
      image: 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1200&q=80',
      accent: '#ff7ab6'
    }
  ];

  constructor(private router: Router) {
    this.updateRouteState(this.router.url);

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateRouteState(event.urlAfterRedirects);
      }
    });
  }

  openModal(modal: Exclude<ModalName, null>): void {
    this.activeModal = modal;
  }

  closeModal(): void {
    this.activeModal = null;
  }

  viewProject(project: ProjectPanel): void {
    if (project.externalUrl) {
      window.location.href = project.externalUrl;
      return;
    }

    if (project.opensChat) {
      this.openChatbot();
      return;
    }

    this.openModal('work');
  }

  openChatbot(): void {
    this.chatBot?.openChat();
  }

  openAbsensi(): void {
    window.location.href = environment.absensiUrl;
  }

  get currentTypingPhrase(): string {
    return this.typingPhrases[this.typingPhraseIndex];
  }

  trackPointer(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.pointerX = ((event.clientX - rect.left) / rect.width) * 100;
    this.pointerY = ((event.clientY - rect.top) / rect.height) * 100;
  }

  openTypingGame(): void {
    this.gameOpen = true;
    this.restartTypingGame();
  }

  closeTypingGame(): void {
    this.gameOpen = false;
  }

  restartTypingGame(): void {
    this.typingInput = '';
    this.typingStartedAt = null;
    this.typingElapsed = 0;
    this.typingWpm = 0;
    this.typingAccuracy = 100;
    this.typingComplete = false;
  }

  nextTypingPhrase(): void {
    this.typingPhraseIndex = (this.typingPhraseIndex + 1) % this.typingPhrases.length;
    this.restartTypingGame();
  }

  onTypingInput(value: string): void {
    if (!this.typingStartedAt) {
      this.typingStartedAt = Date.now();
    }

    this.typingInput = value;
    this.updateTypingStats();
    this.typingComplete = value === this.currentTypingPhrase;
  }

  private updateTypingStats(): void {
    if (!this.typingStartedAt) {
      return;
    }

    const elapsedSeconds = Math.max((Date.now() - this.typingStartedAt) / 1000, 1);
    const correctCharacters = this.countCorrectCharacters(this.typingInput, this.currentTypingPhrase);

    this.typingElapsed = Math.round(elapsedSeconds);
    this.typingAccuracy = this.typingInput.length
      ? Math.round((correctCharacters / this.typingInput.length) * 100)
      : 100;
    this.typingWpm = Math.round((correctCharacters / 5) / (elapsedSeconds / 60));
  }

  private countCorrectCharacters(input: string, target: string): number {
    let total = 0;
    const max = Math.min(input.length, target.length);

    for (let index = 0; index < max; index += 1) {
      if (input[index] === target[index]) {
        total += 1;
      }
    }

    return total;
  }

  private updateRouteState(url: string): void {
    this.isAdminRoute = url.split('?')[0].startsWith('/admin');
  }
}
